import axios, { AxiosResponse } from "axios";
import { TDiversificationCall } from "../screen/Simulation/Utils/DataToDiversificationCall";
import { proxyUrl } from "../config";

export type TOptimizarFondosPayload = {
  isins: string[];
  semanas_atras: number;
  ratio: string;
  periodo: string;
};

export type RatioKeys = "alpha" | "bear_beta" | "beta" | "bull_beta" | "correlation" | "information_ration" | "linear_return" | "log_annualized_return" | "max_drawdown" | "sharpe" | "time_to_recovery" | "tracking_error" | "treynor" | "volatility" | "pca" | "clase_limpia" | "gastos_corrientes";

export type TRatiosKeyCompletos = {
    [key in RatioKeys]: number
};

export type TOptimizarFondosResultados = {
    isin_original: string;
    isin_nuevo: string;
    allfunds_id_original: string;
    allfunds_id_nuevo: string;
    nombre_fondo_original: string;
    nombre_fondo_nuevo: string;
    ratio_seleccion: string;
    valor_ratio_original: number;
    valor_ratio_nuevo: number;
    categoria: string;
    moneda: string;
    posicion_fondo_original: number;
    total_fondos_categoria: number;
    fecha_analisis: Date;
    periodo: string;
    ratios_completos: {
        fondo_original: TRatiosKeyCompletos;
        fondo_nuevo: TRatiosKeyCompletos;
    }
};

export type TOmniaFondosResponse = {
    parametros_entrada: TOptimizarFondosPayload;
    resultados: TOptimizarFondosResultados[]
}

export type TFundIsinAndWeight = {
    isin: string;
    weight: number;
};

export type TPayloadToSimulation = {
    cartera: TFundIsinAndWeight[];
    modelo: TFundIsinAndWeight[];
    saveinstorage: boolean;
    full_ratios: boolean;
};

export type TSerieTemporal = {
    date: Date;
    cartera: number;
    modelo: number;
    fechavlstring: string;
};

export type TRatiosHistoricos = {
    period: string;
    ratio: string;
    cartera: number;
    modelo: number;
    cartmodelo: string;
    portfolioId: string;
    date: Date;
};

type TProcesamiento = {
    num_periodos_serie: number;
    fecha_inicio: Date;
    fecha_fin: Date;
    saveinstorage: boolean;
    full_ratios: boolean;
    pasos_completados: string[];
    analisis_costes_incluido: boolean;
};

type TRendimiento = {
    periodo_analizado: string;
    puntos_datos: number;
};

type TResumenEjecutivo = {
    rendimiento: TRendimiento;
    conclusion: string
};

type TResultados = {
    serie_temporal: TSerieTemporal[];
    ratios_historicos: TRatiosHistoricos[]
};

export type TOmniaSimulationResponse = {
    status: string;
    nomcontrato: string;
    fecha_procesamiento: Date;
    procesamiento: TProcesamiento;
    resultados: TResultados;
    resumen_ejecutivo: TResumenEjecutivo;
    advertencias_globales: string[];
};

export type TMetricasResumen = {
    volatilidad: number;
    mejoria_por_diversificacion: number;
    pct_reduccion_riesgo: number;
    indice_diversificacion: number;
    numero_instrumentos: number;
    fecha: Date;
    volatilidad_cartera: number;
    volatilidad_cartera_corr1: number;
    beneficio_diversificacion_total: number
};

export type TDiversificacionPosiciones = {
    isin: string;
    nominstrum: string;
    tipoactivo1: string;
    tipoactivo2: string;
    familia: string;
    tipoproducto: string;
    divisa: string;
    peso: number;
    volatilidad: number;
    correlacion: number;
    riesgo: number;
    riesgo_pct_total: number;
    beneficio_diversificacion: number;
    pesoxvola: number;
    volaxcor: number;
    diff_vs_non: number;
    aportacion_riesgo_corr1: number;
    aportacion_riesgo_corr_real: number;
    diferencia: number;
};

export type TTotalesTipoActivo1 = {
    tipoactivo1: string;
    peso_total: number;
    volatilidad_corr1: number;
    volatilidad_corr_real: number;
    diferencia: number;
};

export type TTotalesTipoActivo2 = TTotalesTipoActivo1 & {
    tipoactivo2: string;
};

export type TTotalesColumna = {
    peso_total: number;
    volatilidad_corr1: number;
    volatilidad_corr_real: number;
    diferencia: number;
}

export type TTablaDetalleDiversificacion = {
    posiciones: TDiversificacionPosiciones[];
    totales_tipoactivo1: TTotalesTipoActivo1[];
    totales_tipoactivo2: TTotalesTipoActivo2[];
    totales_columna: TTotalesColumna;
};

export type TTartaExterior = {
    tipoactivo1: string;
    valor: number;
    porcentaje: number;
};

export type TTartaInterior = TTartaExterior & {
    tipoactivo2: string;
}

export type TWaterfallDiversificacion = {
    tipo_activo: string;
    valor: number;
}

export type TGraficosDiversificacion = {
    tarta_exterior: TTartaExterior[];
    tarta_interior: TTartaInterior[];
    waterfall: TWaterfallDiversificacion[];
}

export type TDataDiversificacion = {
    nomcontrato: string;
    fecha_calculo: Date;
    metricas_resumen: TMetricasResumen;
    tabla_detalle: TTablaDetalleDiversificacion;
    graficos: TGraficosDiversificacion;
};

export type TDiversificacionSimulacionResponse = {
    status: string;
    data: TDataDiversificacion
};

class OmniaService
{
    public OptimizarFondos = async (payload: TOptimizarFondosPayload): Promise<TOmniaFondosResponse> => {
        const url: string = `${proxyUrl}/omniaServices/OptimizarFondos`;

        try {
            const headers = { "Content-Type": "application/json" };
            const req = await axios.post(url, { ...payload }, { headers });

            if (!req.data) {
                throw new Error(`Error HTTP: ${req.status}`);
            };
            
            return req.data;
        } catch (error) {
            console.error("Error al llamar a la API:", error);
            throw error;
        }
    };

    public SimularFondos = async (payload: TPayloadToSimulation): Promise<TOmniaSimulationResponse> => {
        const url: string = `${proxyUrl}/omniaServices/SimularFondos`;

        try {
            const headers = { "Content-Type": "application/json" };
            const req = await axios.post(url, { ...payload }, { headers });

            if (!req.data) {
                throw new Error(`Error HTTP: ${req.status}`);
            };
            
            return req.data;
        } catch (error) {
            console.error("Error al llamar a la API:", error);
            throw error;
        }
    };

    public DiversificacionCartera = async (payload: TDiversificationCall): Promise<TDataDiversificacion> => {
        const url: string = `${proxyUrl}/omniaServices/DiversificarFondos`;

        try {
            const headers = { "Content-Type": "application/json" };
            const req = await axios.post(url, { ...payload }, { headers });

            if (!req.data) {
                throw new Error(`Error HTTP: ${req.status}`);
            };
            
            return req.data;
        } catch (error) {
            console.error("Error al llamar a la API:", error);
            throw error;
        }
    };
};

const OmniaServices = new OmniaService()
export default OmniaServices;
